import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Container,
  Paper,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  TablePagination,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Snackbar,
  Breadcrumbs,
  Link,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LinkIcon from '@mui/icons-material/Link';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { timeAgo } from '../utils/relativeTime';
import {
  getOrganization,
  getOrganizationMembers,
  updateOrganizationMember,
  removeOrganizationMember,
  createOrganizationInvite,
  generateInviteLink,
} from '../utils/organizationsApi';
import { createTeam, getOrganizationTeams, deleteTeam } from '../utils/teamsApi';

// Tab identity lives in the URL as ?tab=<slug> so each tab is a shareable link.
// Members is first, so it is the default landing tab when no slug is present.
const TAB_SLUGS = ['members', 'teams'];

const OrganizationDetailPage = () => {
  const { orgId } = useParams();
  const { user } = useAuth();
  const { setCurrentOrganization } = useWorkspace();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL drives the open tab; an unknown/absent slug lands on Members (0).
  const tabIndex = TAB_SLUGS.indexOf(searchParams.get('tab'));
  const activeTab = tabIndex === -1 ? 0 : tabIndex;
  const handleTabChange = (_, value) => {
    setSearchParams((prev) => {
      prev.set('tab', TAB_SLUGS[value]);
      return prev;
    });
  };

  // Team creation
  const [createTeamDialogOpen, setCreateTeamDialogOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);

  // Invite dialog
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);

  // Member actions
  const [memberMenuAnchor, setMemberMenuAnchor] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // Member pagination
  const [memberPage, setMemberPage] = useState(0);
  const [memberRowsPerPage, setMemberRowsPerPage] = useState(10);

  const handleMemberPageChange = (_, newPage) => {
    setMemberPage(newPage);
  };

  const handleMemberRowsPerPageChange = (event) => {
    setMemberRowsPerPage(parseInt(event.target.value, 10));
    setMemberPage(0);
  };

  // Team actions
  const [teamMenuAnchor, setTeamMenuAnchor] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [deleteTeamDialogOpen, setDeleteTeamDialogOpen] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState(false);

  const [successMessage, setSuccessMessage] = useState(null);
  const [actionError, setActionError] = useState(null);

  const fetchOrganizationData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [orgData, membersData, teamsData] = await Promise.all([
        getOrganization(orgId),
        getOrganizationMembers(orgId),
        getOrganizationTeams(orgId),
      ]);
      setOrganization(orgData);
      setMembers(membersData);
      setTeams(teamsData);
      setCurrentOrganization(orgData);
    } catch (err) {
      console.error('Error fetching organization:', err);
      setError(err.response?.data?.message || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }, [orgId, setCurrentOrganization]);

  useEffect(() => {
    fetchOrganizationData();
  }, [fetchOrganizationData]);

  // Keep the member page in range if the list shrinks (e.g. after a removal).
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(members.length / memberRowsPerPage) - 1);
    if (memberPage > lastPage) {
      setMemberPage(lastPage);
    }
  }, [members.length, memberRowsPerPage, memberPage]);

  const canManage = organization && ['owner', 'admin'].includes(organization.userRole);
  const isOwner = organization && organization.userRole === 'owner';

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;

    setCreatingTeam(true);
    try {
      await createTeam(orgId, {
        name: newTeamName.trim(),
        description: newTeamDescription.trim() || undefined,
      });
      setSuccessMessage('Team created successfully!');
      setCreateTeamDialogOpen(false);
      setNewTeamName('');
      setNewTeamDescription('');
      fetchOrganizationData();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to create team');
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleCreateInvite = async () => {
    setCreatingInvite(true);
    try {
      const invite = await createOrganizationInvite(orgId);
      setInviteLink(generateInviteLink(invite.token));
      setInviteDialogOpen(true);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to create invite');
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setSuccessMessage('Invite link copied to clipboard!');
  };

  const handleMemberMenuOpen = (event, member) => {
    setMemberMenuAnchor(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMemberMenuClose = () => {
    setMemberMenuAnchor(null);
    setSelectedMember(null);
  };

  const handleChangeRole = async (newRole) => {
    if (!selectedMember) return;
    try {
      await updateOrganizationMember(orgId, selectedMember.userId, { role: newRole });
      setSuccessMessage('Member role updated');
      fetchOrganizationData();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update role');
    }
    handleMemberMenuClose();
  };

  const handleRemoveMember = async () => {
    if (!selectedMember) return;
    try {
      await removeOrganizationMember(orgId, selectedMember.userId);
      setSuccessMessage('Member removed');
      fetchOrganizationData();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to remove member');
    }
    handleMemberMenuClose();
  };

  const handleOpenTeam = (team) => {
    navigate(`/teams/${team.id}`);
  };

  const handleTeamMenuOpen = (event, team) => {
    event.stopPropagation();
    setTeamMenuAnchor(event.currentTarget);
    setSelectedTeam(team);
  };

  const handleTeamMenuClose = () => {
    setTeamMenuAnchor(null);
    setSelectedTeam(null);
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    setDeletingTeam(true);
    try {
      await deleteTeam(selectedTeam.id);
      setSuccessMessage('Team deleted');
      setDeleteTeamDialogOpen(false);
      handleTeamMenuClose();
      fetchOrganizationData();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to delete team');
    } finally {
      setDeletingTeam(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
      </Container>
    );
  }

  if (!organization) return null;

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/organizations" underline="hover" color="inherit">
            Organizations
          </Link>
          <Typography color="text.primary">{organization.name}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {organization.logo ? (
            <Avatar src={organization.logo} sx={{ width: 64, height: 64, mr: 2 }} />
          ) : (
            <Avatar sx={{ width: 64, height: 64, mr: 2, bgcolor: 'primary.main' }}>
              <BusinessIcon sx={{ fontSize: 32 }} />
            </Avatar>
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4">{organization.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {organization.slug} - {members.length} members - {teams.length} teams
            </Typography>
          </Box>
          {canManage && (
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              component={RouterLink}
              to={`/organizations/${orgId}/settings`}
            >
              Settings
            </Button>
          )}
        </Box>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Members" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Teams" icon={<GroupIcon />} iconPosition="start" />
        </Tabs>

        {/* Teams Tab */}
        {activeTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Teams</Typography>
              {canManage && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateTeamDialogOpen(true)}
                >
                  Create Team
                </Button>
              )}
            </Box>

            {teams.length > 0 ? (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 2,
              }}>
                {teams.map((team) => (
                  <Card
                    key={team.id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 6 },
                    }}
                    onClick={() => handleOpenTeam(team)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>{team.name}</Typography>
                        {canManage && (
                          <IconButton
                            size="small"
                            sx={{ mt: -0.5, mr: -0.5 }}
                            onClick={(e) => handleTeamMenuOpen(e, team)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {team.description || 'No description'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {team.memberCount} members
                        </Typography>
                        {team.userRole && (
                          <Chip
                            label={team.userRole}
                            size="small"
                            color={getRoleBadgeColor(team.userRole)}
                            sx={{ ml: 'auto' }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <GroupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No teams yet</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Members Tab */}
        {activeTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Members</Typography>
              {canManage && (
                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={handleCreateInvite}
                  disabled={creatingInvite}
                >
                  {creatingInvite ? <CircularProgress size={24} /> : 'Create Invite Link'}
                </Button>
              )}
            </Box>

            <List>
              {members
                .slice(
                  memberPage * memberRowsPerPage,
                  memberPage * memberRowsPerPage + memberRowsPerPage
                )
                .map((member) => (
                  <ListItem key={member.id}>
                    <ListItemAvatar>
                      <Avatar>{member.user?.name?.charAt(0) || '?'}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.user?.name || 'Unknown'}
                      secondary={
                        <>
                          {member.user?.email}
                          {' · '}
                          {member.user?.lastLoginAt
                            ? `Last login ${timeAgo(member.user.lastLoginAt)}`
                            : 'Never logged in'}
                        </>
                      }
                    />
                    <Chip
                      label={member.role}
                      size="small"
                      color={getRoleBadgeColor(member.role)}
                      sx={{ mr: 1 }}
                    />
                    {canManage && member.userId !== user?.id && (
                      <ListItemSecondaryAction>
                        <IconButton onClick={(e) => handleMemberMenuOpen(e, member)}>
                          <MoreVertIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
            </List>

            {members.length > memberRowsPerPage && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={members.length}
                rowsPerPage={memberRowsPerPage}
                page={memberPage}
                onPageChange={handleMemberPageChange}
                onRowsPerPageChange={handleMemberRowsPerPageChange}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Create Team Dialog */}
      <Dialog open={createTeamDialogOpen} onClose={() => setCreateTeamDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Team</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Team Name"
            fullWidth
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            required
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={newTeamDescription}
            onChange={(e) => setNewTeamDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTeamDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateTeam}
            variant="contained"
            disabled={creatingTeam || !newTeamName.trim()}
          >
            {creatingTeam ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Link Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Organization Invite Link</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Share this link to invite people to your organization.
          </DialogContentText>
          <TextField
            fullWidth
            value={inviteLink}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Close</Button>
          <Button onClick={handleCopyInviteLink} variant="contained">
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Member Actions Menu */}
      <Menu
        anchorEl={memberMenuAnchor}
        open={Boolean(memberMenuAnchor)}
        onClose={handleMemberMenuClose}
      >
        {isOwner && selectedMember?.role !== 'owner' && (
          <MenuItem onClick={() => handleChangeRole('owner')}>Make Owner</MenuItem>
        )}
        {selectedMember?.role !== 'admin' && (
          <MenuItem onClick={() => handleChangeRole('admin')}>Make Admin</MenuItem>
        )}
        {selectedMember?.role !== 'member' && (
          <MenuItem onClick={() => handleChangeRole('member')}>Make Member</MenuItem>
        )}
        {selectedMember?.role !== 'viewer' && (
          <MenuItem onClick={() => handleChangeRole('viewer')}>Make Viewer</MenuItem>
        )}
        <MenuItem onClick={handleRemoveMember} sx={{ color: 'error.main' }}>
          Remove from Organization
        </MenuItem>
      </Menu>

      {/* Team Actions Menu */}
      <Menu
        anchorEl={teamMenuAnchor}
        open={Boolean(teamMenuAnchor)}
        onClose={handleTeamMenuClose}
      >
        <MenuItem
          onClick={() => { setTeamMenuAnchor(null); setDeleteTeamDialogOpen(true); }}
          sx={{ color: 'error.main' }}
        >
          Delete Team
        </MenuItem>
      </Menu>

      {/* Delete Team Confirmation Dialog */}
      <Dialog
        open={deleteTeamDialogOpen}
        onClose={() => { setDeleteTeamDialogOpen(false); handleTeamMenuClose(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedTeam?.name}</strong>? This will
            remove the team and all its memberships. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteTeamDialogOpen(false); handleTeamMenuClose(); }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTeam} color="error" variant="contained" disabled={deletingTeam}>
            {deletingTeam ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage || !!actionError}
        autoHideDuration={6000}
        onClose={() => { setSuccessMessage(null); setActionError(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={successMessage ? 'success' : 'error'}>
          {successMessage || actionError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationDetailPage;
